import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ApiService from "../../services/ApiService";
import { formatCurrency, formatNumber } from "../../utils/formatUtils";
import { SPONSOR_LOGO_PLACEMENT_ANALYSIS } from "../keys";

const useSponsorLogoPlacementAnalysis = (sponsorObjectId) => {
  const { t } = useTranslation();
  const tenant = useSelector((state) => state.auth.tenant);
  const date = useSelector((state) => state.date);
  const selectedCurrency = useSelector((state) => state.currency.selectedCurrency);

  const { startDate, endDate } = date;
  const enabled = !!(date.startDate && date.endDate);
  let since;
  let until;
  if (enabled) {
    since = format(date.startDate, "yyyy-MM-dd");
    until = format(date.endDate, "yyyy-MM-dd");
  }

  return useQuery({
    queryKey: [
      SPONSOR_LOGO_PLACEMENT_ANALYSIS,
      sponsorObjectId,
      tenant.id,
      startDate,
      endDate,
    ],
    queryFn: () =>
      ApiService.getSponsorLogoPlacementAnalysis(since, until, sponsorObjectId),
    select: (data) => {
      const analysisData = data.data;
      
      if (!analysisData.data || analysisData.data.length === 0) {
        return {
          headers: [
            { Header: t("Placement"), accessor: "placement_category" },
            { Header: t("Brand"), accessor: "logo_class" },
            { Header: t("Exposures"), accessor: "total_exposures" },
            { Header: t("Exposure Time"), accessor: "total_exposure_time_minutes" },
            { Header: t("Reach"), accessor: "total_reach" },
            { Header: t("Total Media Value"), accessor: "estimated_value" },
          ],
          rows: [],
          summary: {
            total_categories: 0,
            total_exposures: 0,
            total_exposure_time: 0,
            total_value: 0
          }
        };
      }

      const rows = analysisData.data.map((row) => ({
        placement_category: row.placement_category,
        logo_class: row.logo_class,
        total_exposures: formatNumber(row.total_exposures, selectedCurrency, "0,0"),
        total_exposure_time_minutes: `${row.total_exposure_time_minutes} min`,
        total_reach: formatNumber(row.total_reach, selectedCurrency, "0,0"),
        estimated_value: formatCurrency(row.estimated_value, selectedCurrency),
      }));

      return {
        headers: [
          { Header: t("Placement"), accessor: "placement_category" },
          { Header: t("Brand"), accessor: "logo_class" },
          { Header: t("Exposures"), accessor: "total_exposures" },
          { Header: t("Exposure Time"), accessor: "total_exposure_time_minutes" },
          { Header: t("Reach"), accessor: "total_reach" },
          { Header: t("Total Media Value"), accessor: "estimated_value" },
        ],
        rows,
        summary: {
          total_categories: analysisData.summary.total_categories,
          total_exposures: formatNumber(analysisData.summary.total_exposures, selectedCurrency, "0,0"),
          total_exposure_time: `${analysisData.summary.total_exposure_time_minutes} min`,
          total_value: formatCurrency(analysisData.summary.total_value, selectedCurrency)
        }
      };
    },
    enabled,
    placeholderData: { 
      data: { 
        headers: [
          { Header: t("Placement"), accessor: "placement_category" },
          { Header: t("Brand"), accessor: "logo_class" },
          { Header: t("Exposures"), accessor: "total_exposures" },
          { Header: t("Exposure Time"), accessor: "total_exposure_time_minutes" },
          { Header: t("Reach"), accessor: "total_reach" },
          { Header: t("Total Media Value"), accessor: "estimated_value" },
        ], 
        rows: [],
        summary: {
          total_categories: 0,
          total_exposures: 0,
          total_exposure_time: 0,
          total_value: 0
        }
      } 
    },
  });
};

export default useSponsorLogoPlacementAnalysis;